/**
 * RAM — Retail Account Maintenance routes.
 *
 *   /ram                        → search-first landing
 *   /ram/wizard/:action         → dispatches to the appropriate action form
 */
import { Routes, Route, Navigate, useParams, useSearchParams } from 'react-router';
import { SplatRedirect } from '@/components/common/SplatRedirect';
import RamLanding from './RamLanding';
import { MaintenanceWizard } from './MaintenanceWizard';
import { MaintActionsLanding } from '@/modules/Maint/MaintActionsLanding';
import type { RamActionCode, RamTarget } from './types';
import { RAM_ACTIONS } from './types';

const VALID_ACTION_CODES = new Set<string>(RAM_ACTIONS.map((a) => a.code));

function WizardRoute(): JSX.Element {
  const { action } = useParams<{ action: string }>();
  const [search] = useSearchParams();

  if (!action || !VALID_ACTION_CODES.has(action)) {
    return <Navigate to="/ram" replace />;
  }

  const target: RamTarget = {
    cifId: search.get('cifId') ?? '',
    customerName: search.get('name') ?? undefined,
    acctId: search.get('acctId') ?? undefined,
    acctType: search.get('acctType') ?? undefined,
  };

  if (!target.cifId) {
    return <Navigate to="/ram" replace />;
  }

  return (
    <div className="ram-page">
      <MaintenanceWizard
        action={action as RamActionCode}
        target={target}
        onExit={() => undefined}
      />
    </div>
  );
}

export function RamRoutes(): JSX.Element {
  return (
    <Routes>
      <Route index element={<RamLanding />} />
      <Route path="wizard/:action" element={<WizardRoute />} />
      {/* Wave 6.3 — list of MaintActions awaiting close-out (parallel to RAO's
          in-flight cart cards). Bill 2026-04-25: any MaintAction the FSR
          started but didn't resolve shows up here for cleanup with the
          CloseMaintActionModal. */}
      <Route path="in-flight" element={<MaintActionsLanding />} />
      <Route path="*" element={<SplatRedirect />} />
    </Routes>
  );
}
